import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Sparkles, Zap, Wrench, Gift, ChevronDown } from 'lucide-react'
import { CHANGELOG } from '@/constants/changelog'
import type { ChangelogChange, ChangelogEntry } from '@/constants/changelog'

const TYPE_LABELS: Record<ChangelogChange['type'], { key: string; fallback: string }> = {
  feature: { key: 'changelog.typeNew', fallback: 'Neu' },
  improvement: { key: 'changelog.typeImproved', fallback: 'Verbessert' },
  fix: { key: 'changelog.typeFixed', fallback: 'Behoben' },
}

const TYPE_STYLE = {
  feature: { icon: Gift, color: 'text-emerald-600', bg: 'bg-emerald-100' },
  improvement: { icon: Zap, color: 'text-blue-600', bg: 'bg-blue-100' },
  fix: { icon: Wrench, color: 'text-amber-600', bg: 'bg-amber-100' },
} as const

interface Props {
  open: boolean
  onClose: () => void
}

function ChangeItem({ change, lang }: { change: ChangelogChange; lang: string }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const style = TYPE_STYLE[change.type]
  const label = TYPE_LABELS[change.type]
  const Icon = style.icon

  // Pick localized text with German fallback
  const text = (lang !== 'de' && change.translations?.[lang]?.text) || change.text
  const detail = (lang !== 'de' && change.translations?.[lang]?.detail) || change.detail
  const hasDetail = !!detail

  return (
    <li>
      <button
        data-testid="whats-new-modal-expanded-btn"
        type="button"
        onClick={() => hasDetail && setExpanded(!expanded)}
        className={`flex items-start gap-2 w-full text-left ${hasDetail ? 'cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1 rounded-lg' : 'py-0.5'}`}
      >
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${style.bg} ${style.color} shrink-0 mt-0.5`}>
          <Icon size={10} />
          {t(label.key, label.fallback)}
        </span>
        <span className="text-sm text-gray-700 flex-1">{text}</span>
        {hasDetail && (
          <ChevronDown size={14} className={`text-gray-400 shrink-0 mt-1 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        )}
      </button>
      {expanded && detail && (
        <div className="ml-[4.5rem] mt-1 mb-2 text-xs text-gray-500 leading-relaxed bg-gray-50 rounded-lg px-3 py-2">
          {detail}
        </div>
      )}
    </li>
  )
}

/** Format a date string like "23. März 2026" to locale-aware format */
function formatDate(dateStr: string, lang: string): string {
  // Parse German date format "DD. Monat YYYY" or variants
  const match = dateStr.match(/(\d{1,2})\.\s*(\w+)\s+(\d{4})/)
  if (!match) return dateStr

  const months: Record<string, number> = {
    'Januar': 0, 'Februar': 1, 'März': 2, 'April': 3,
    'Mai': 4, 'Juni': 5, 'Juli': 6, 'August': 7,
    'September': 8, 'Oktober': 9, 'November': 10, 'Dezember': 11,
  }
  const monthNum = months[match[2]]
  if (monthNum === undefined) return dateStr

  const date = new Date(parseInt(match[3]), monthNum, parseInt(match[1]))
  const locale = lang === 'de' ? 'de-CH' : lang === 'fr' ? 'fr-CH' : lang === 'it' ? 'it-CH' : 'en-GB'
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
}

export default function WhatsNewModal({ open, onClose }: Props) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language?.split('-')[0] || 'de'

  // Group changelog entries by date to avoid repeating date headers.
  // Duplicate change texts (same text in several versions) are shown only once —
  // CHANGELOG is newest first, so the newest occurrence wins.
  const grouped = useMemo(() => {
    const groups: { date: string; entries: ChangelogEntry[] }[] = []
    const seen = new Set<string>()
    for (const entry of CHANGELOG) {
      const changes = entry.changes.filter((change) => {
        if (seen.has(change.text)) return false
        seen.add(change.text)
        return true
      })
      if (changes.length === 0) continue

      const deduped: ChangelogEntry = { ...entry, changes }
      const last = groups[groups.length - 1]
      if (last && last.date === entry.date) {
        last.entries.push(deduped)
      } else {
        groups.push({ date: entry.date, entries: [deduped] })
      }
    }
    return groups
  }, [])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="fixed inset-0 bg-black/40" />
      <div
        className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Sparkles size={20} className="text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              {t('changelog.title', 'Neuigkeiten')}
            </h2>
          </div>
          <button
            data-testid="whats-new-modal-close-btn-1"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content — grouped by date */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-6 divide-y divide-gray-100">
          {grouped.map((group) => (
            <div key={group.date} className="pt-4 first:pt-0">
              <div className="text-sm font-semibold text-gray-900 mb-3">
                {formatDate(group.date, lang)}
              </div>
              <div className="space-y-1">
                {group.entries.flatMap((entry) =>
                  entry.changes.map((change, i) => (
                    <ul key={`${entry.version}-${i}`} className="space-y-1">
                      <ChangeItem change={change} lang={lang} />
                    </ul>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100">
          <button
            data-testid="whats-new-modal-close-btn-2"
            onClick={onClose}
            className="w-full py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            {t('common.close', 'Schliessen')}
          </button>
        </div>
      </div>
    </div>
  )
}
